import { useCallback, useRef, useState } from 'react'

interface SaveThenOptions {
  /**
   * Stay put when the flush fails. Set for going forward, where moving on is
   * what loses a refused row (SCRUM-1032 QC item 14); not for Back, Exit or an
   * earlier step, which are the user leaving on purpose.
   */
  holdOnFailure?: boolean
  /**
   * The header's ×. Never dropped: asked for while a flush runs, it is queued
   * and taken when that flush ends, whatever the flush answered.
   */
  isExit?: boolean
}

interface UseGuidedSaveThenArgs {
  /** Flushes every grid; throws when anything was not written. */
  flush: () => Promise<unknown>
  /** The user left a step whose flush failed. */
  onLeftWithFailure: (error: unknown) => void
  /** A forward move was held by a failed flush. */
  onHeld?: (error: unknown) => void
  /** Exit was asked for while a flush is running, and will follow it. */
  onExitQueued?: () => void
}

/**
 * SCRUM-1032 — save first, move second.
 *
 * A second move asked for mid-flush used to be dropped without a word, and
 * that included Exit: with a write the server never answered, the header's ×
 * did nothing at all (wizard review). Exit is now queued behind the flush, and
 * the flush itself is bounded (`GUIDED_SAVE_TIMEOUT_MS`), so the user always
 * gets out.
 *
 * Resolves true when the action ran after a clean flush.
 */
const useGuidedSaveThen = ({
  flush,
  onLeftWithFailure,
  onHeld,
  onExitQueued,
}: UseGuidedSaveThenArgs) => {
  const [isSaving, setIsSaving] = useState(false)
  const isSavingRef = useRef(false)
  const queuedExitRef = useRef<(() => void) | null>(null)
  const argsRef = useRef({ flush, onLeftWithFailure, onHeld, onExitQueued })
  argsRef.current = { flush, onLeftWithFailure, onHeld, onExitQueued }

  const saveThen = useCallback(
    async (action: () => void, options?: SaveThenOptions): Promise<boolean> => {
      const args = argsRef.current
      if (isSavingRef.current) {
        if (options?.isExit) {
          queuedExitRef.current = action
          args.onExitQueued?.()
        }
        return false
      }
      isSavingRef.current = true
      setIsSaving(true)
      try {
        await args.flush()
        const exit = queuedExitRef.current
        queuedExitRef.current = null
        ;(exit ?? action)()
        return !exit
      } catch (error) {
        const exit = queuedExitRef.current
        queuedExitRef.current = null
        if (!exit && options?.holdOnFailure) {
          argsRef.current.onHeld?.(error)
          return false
        }
        ;(exit ?? action)()
        argsRef.current.onLeftWithFailure(error)
        return false
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    },
    [],
  )

  return { saveThen, isSaving, isSavingRef }
}

export default useGuidedSaveThen
